import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CloudSun, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export const LEVEL_STYLE = {
  LOW: "bg-muted text-muted-foreground",
  MEDIUM: "bg-accent/20 text-accent-foreground",
  HIGH: "bg-orange-100 text-orange-900",
  CRITICAL: "bg-destructive/15 text-destructive",
};
export const LEVEL_LABEL = { LOW: "Düşük", MEDIUM: "Orta", HIGH: "Yüksek", CRITICAL: "Kritik" };

export const dayName = (iso) =>
  new Date(iso).toLocaleDateString("tr-TR", { weekday: "short", day: "2-digit", month: "short" });

export const updatedAt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("tr-TR", {
        day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "-";

export function useWeather(params) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const query = JSON.stringify(params || {});

  const load = useCallback(
    async (refresh = false) => {
      const p = JSON.parse(query);
      if (!p || (!p.farm_id && !p.province && p.latitude == null)) return;
      try {
        const { data: d } = await api.get("/weather", { params: { ...p, refresh } });
        setData(d);
        setError(null);
      } catch (e) {
        setError(e?.response?.data?.detail || "Hava durumu alınamadı");
      }
    },
    [query],
  );

  useEffect(() => { load(); }, [load]);
  return { data, error, reload: load };
}

/** Ana sayfa / çiftlik paneli için kompakt hava kartı. */
export function WeatherWidget({ params, title, compact = false }) {
  const { data, error, reload } = useWeather(params);

  if (error)
    return (
      <div className="rounded-lg border border-border bg-card p-5" data-testid="weather-widget-error">
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  if (!data)
    return (
      <div className="h-40 animate-pulse rounded-lg border border-border bg-card" data-testid="weather-widget-loading" />
    );

  const cur = data.current || {};
  const today = (data.forecast || [])[0] || {};
  const topRisks = (data.risks || []).filter((r) => ["HIGH", "CRITICAL"].includes(r.level)).slice(0, 3);

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card" data-testid="weather-widget">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title || "Hava Durumu"}
          </p>
          <p className="font-display text-lg font-extrabold text-primary" data-testid="weather-location">
            {data.location?.province ? `${data.location.province} / ` : ""}
            {data.location?.label || "Konum"}
          </p>
          <div className="mt-2 flex items-end gap-3">
            <CloudSun className="h-8 w-8 text-secondary" />
            <span className="font-display text-4xl font-extrabold leading-none text-primary" data-testid="weather-temp">
              {cur.temperature != null ? `${Math.round(cur.temperature)}°C` : "—"}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="weather-summary">
            En düşük {today.temp_min != null ? `${Math.round(today.temp_min)}°C` : "—"} · En yüksek{" "}
            {today.temp_max != null ? `${Math.round(today.temp_max)}°C` : "—"} · Rüzgâr{" "}
            {cur.wind_kmh != null ? `${Math.round(cur.wind_kmh)} km/s` : "—"} · Nem{" "}
            {cur.humidity != null ? `%${Math.round(cur.humidity)}` : "—"}
          </p>
        </div>
        <button
          onClick={() => reload(true)}
          data-testid="weather-refresh-btn"
          className="rounded-lg border border-border p-2 text-muted-foreground transition-colors duration-150 hover:text-primary"
          aria-label="Yenile"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {!compact && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto p-4" data-testid="weather-daily-strip">
          {(data.forecast || []).map((d) => (
            <div
              key={d.date}
              data-testid={`weather-day-${d.date}`}
              className="min-w-[92px] rounded-lg border border-border bg-background p-3 text-center"
            >
              <p className="text-xs font-semibold text-muted-foreground">{dayName(d.date)}</p>
              <p className="mt-1 font-display text-base font-bold text-primary">
                {d.temp_max != null ? `${Math.round(d.temp_max)}°` : "—"}
              </p>
              <p className="text-xs text-muted-foreground">
                {d.temp_min != null ? `${Math.round(d.temp_min)}°` : "—"}
              </p>
              {d.rain_probability != null && (
                <p className="mt-1 text-[11px] text-secondary">%{d.rain_probability} yağış</p>
              )}
            </div>
          ))}
        </div>
      )}

      {!!topRisks.length && (
        <div className="space-y-2 border-t border-border p-4" data-testid="weather-widget-risks">
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase text-primary">
            <AlertTriangle className="h-3.5 w-3.5" /> Çiftliğiniz İçin Bugün
          </p>
          {topRisks.map((r) => (
            <div
              key={`${r.code}-${r.date}`}
              data-testid={`weather-risk-${r.code}`}
              className={`rounded-lg p-3 text-sm ${LEVEL_STYLE[r.level]}`}
            >
              <p className="font-bold">
                {r.icon} {r.label} · {LEVEL_LABEL[r.level]}
              </p>
              <p className="mt-0.5 text-xs">{r.message}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-4 py-3">
        <p className="text-[11px] leading-snug text-muted-foreground" data-testid="weather-source">
          Kaynak: {data.source_label}
          <br />
          Son Güncelleme: {updatedAt(data.retrieved_at)}
          {data.status === "STALE" && (
            <>
              <br />
              <span className="font-semibold text-destructive">
                Hava verisi güncellenemedi. Son başarılı tahmin gösteriliyor.
              </span>
            </>
          )}
        </p>
        <Link
          to="/hava"
          data-testid="weather-detail-link"
          className="rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"
        >
          Detaylı Hava Durumu
        </Link>
      </div>
    </div>
  );
}
